import { http } from './api';
import { captureModuleException, Module } from 'app/_modules/shared/utils/captureModuleException';

import { StatusCodes } from 'http-status-codes';
import { testScenarioCookieName, testScenarioHeaderName } from 'shared/src/__tests__/common/testFixtures';
import { CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT } from 'shared/src/observability/constants';

import type { CommonAuthProviderContextValue } from 'app/_modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import type { RefObject } from 'react';
import type { XiorError, XiorInterceptorRequestConfig } from 'xior';

export type AuthRef = RefObject<Pick<CommonAuthProviderContextValue, 'getAccessToken' | 'loginWithRedirect'>>;

function inferModuleFromUrl(url: string | undefined): Module {
  if (url?.includes('/billing')) {
    return Module.FINOPS;
  }

  if (url?.includes('/sandboxes')) {
    return Module.SANDBOXES;
  }

  if (url?.includes('/notifications')) {
    return Module.NOTIFICATIONS;
  }

  return Module.FINOPS;
}

/**
 * Registers xior request/response interceptors on the shared Zeus API client.
 * Must be called once from a React component that sits inside CommonAuthProvider.
 * Returns an eject function that removes the interceptors (used for cleanup in useEffect).
 *
 * Auth functions are read from a ref on every request so the interceptors never
 * need to be re-registered when auth state changes.
 */
export function setupZeusInterceptors(authRef: AuthRef): () => void {
  http.defaults.baseURL = process.env.ZEUS_API_URL;

  const requestInterceptor = http.interceptors.request.use(async (config: XiorInterceptorRequestConfig) => {
    const token = await authRef.current.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers[CRIBL_AGENT_HEADER_NAME] = MAESTRO_AGENT;

    const testScenario = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${testScenarioCookieName}=`))
      ?.split('=')[1];

    if (testScenario) {
      config.headers[testScenarioHeaderName] = testScenario;
    }

    return config;
  });

  const responseInterceptor = http.interceptors.response.use(
    (response) => response,
    async (error: XiorError) => {
      const status = error.response?.status;
      const url = error.config?.url;

      const shouldCaptureException =
        status === StatusCodes.UNAUTHORIZED || (status !== undefined && status >= StatusCodes.INTERNAL_SERVER_ERROR);

      if (shouldCaptureException) {
        captureModuleException({
          module: inferModuleFromUrl(url),
          source: 'setupZeusAxios.hook',
          context: {
            method: error.config?.method,
            url,
            status,
          },
          error: error.message,
        });
      }

      if (status === StatusCodes.UNAUTHORIZED) {
        await authRef.current.loginWithRedirect({ returnTo: window.location.toString() });

        return;
      }

      return Promise.reject(error);
    },
  );

  return () => {
    http.interceptors.request.eject(requestInterceptor);
    http.interceptors.response.eject(responseInterceptor);
  };
}
